import { GoBackButton } from 'components/Button/GoBackButton';
import LoaderButton from 'components/Button/LoaderButton';
import { InstructionCallout } from 'components/Instruction/InstructionCallout';
import StepIndicator from 'components/StepIndicator/StepIndicator';
import COLORS from 'constants/colors';
import { useEffect, useState } from 'react';
import { useTrackConversionStep } from 'services/track-conversion';
import styled from 'styled-components';
import NoticeMessage from '../Messages/Notice';

const Divider = styled.hr<{ spacing?: 'sm' | 'md' }>`
  width: 100%;
  background-color: #d9d9d9;
  height: 1px;
  border-width: 0;
  margin: 0;
  margin-bottom: ${(props) => (props.spacing === 'md' ? 8 : 0)}px;
`;

const Instructions = styled.div<{ spacing?: 'sm' | 'md' }>`
  p {
    color: var(--Updated-Finch-Black, #161616);
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 21px */
    margin: 0;
    margin-bottom: ${(props) => (props.spacing === 'md' ? 24 : 16)}px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  a {
    font-family: Inter, sans-serif;
    font-weight: 600;
    text-decoration: underline;
    color: ${COLORS.BLACK};
  }
  strong {
    font-weight: 600;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
`;

const StepTitle = styled.h2<{ centered?: boolean }>`
  color: var(--Updated-Finch-Black, #161616);
  font-family: Inter;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  text-align: ${(props) => (props.centered ? 'center' : 'left')};
`;

const StepCounter = styled.div`
  color: var(--Updated-Finch-Black, #161616);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  text-align: center;
  margin-bottom: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: auto;
`;

export type MultiSignInStep = {
  id: string;
  title?: string;
  instructions?: JSX.Element | string;
  instructionsLink?: string;
  fieldNames: string[];
  welcomePage?: boolean;
  spacing?: 'sm' | 'md';
  divider?: boolean;
};

type MultiSignInBaseProps = {
  steps: MultiSignInStep[];
  onSubmit: (fieldToValueMap: Record<string, string>) => Promise<void>;
  render: (
    fields: Record<
      string,
      {
        value: string;
        invalid: boolean;
        onChange: (value: string, invalid?: boolean) => void;
      }
    >,
    currentStep: number,
  ) => React.ReactNode;
  isLoading: boolean;
  formLabel?: string;
  notice?: string;
  stepCounterLabel?: string;
};

const MultiSignInBase = ({
  steps,
  onSubmit,
  render,
  isLoading,
  formLabel,
  notice,
  stepCounterLabel = 'Step',
}: MultiSignInBaseProps): JSX.Element => {
  const [currentStep, setCurrentStep] = useState(0);
  const trackConversion = useTrackConversionStep();

  // Initialize form state with all fields from all steps
  const [formState, setFormState] = useState<
    Record<string, { value: string; invalid: boolean }>
  >(() => {
    const initialState: Record<string, { value: string; invalid: boolean }> =
      {};
    steps.forEach((step) => {
      step.fieldNames.forEach((fieldName) => {
        initialState[fieldName] = { value: '', invalid: false };
      });
    });
    return initialState;
  });

  const currentStepConfig = steps[currentStep];

  // Track display event when reaching each step
  useEffect(() => {
    trackConversion({
      action: 'display',
      substep: currentStepConfig.id,
    });
  }, [currentStep, currentStepConfig.id, trackConversion]);

  const getOnChange =
    (fieldName: string) => (value: string, invalid?: boolean) => {
      setFormState((prev) => ({
        ...prev,
        [fieldName]: { value, invalid: invalid ?? false },
      }));
    };

  const isFieldInvalid = (fieldName: string) => {
    const field = formState[fieldName];
    return field.value.length === 0 || field.invalid;
  };

  const validateCurrentStep = () => {
    // Welcome pages have no validation
    if (currentStepConfig.welcomePage) return true;

    const invalidFields = currentStepConfig.fieldNames.filter(isFieldInvalid);
    return invalidFields.length === 0;
  };

  const handleFinalSubmit = () => {
    const fieldToValueMap: Record<string, string> = {};
    Object.keys(formState).forEach((fieldName) => {
      const value = formState[fieldName].value;
      fieldToValueMap[fieldName] =
        typeof value === 'string' ? value.trim() : value;
    });
    onSubmit(fieldToValueMap);
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      // Mark invalid fields in state
      setFormState((prev) => {
        const updated = { ...prev };
        currentStepConfig.fieldNames.forEach((fieldName) => {
          if (isFieldInvalid(fieldName)) {
            updated[fieldName] = { ...prev[fieldName], invalid: true };
          }
        });
        return updated;
      });
      return;
    }

    // Track submit event for the current step
    trackConversion({
      action: 'submit',
      substep: currentStepConfig.id,
    });

    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleFinalSubmit();
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleNext();
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const previousStep = currentStep - 1;
      setCurrentStep(previousStep);
    }
  };

  const getButtonText = () => {
    if (currentStepConfig.welcomePage) return 'Continue';
    if (currentStep === steps.length - 1) return 'Connect';
    return 'Continue';
  };

  const shouldShowBackButton = () => {
    return currentStep > 0 && !isLoading;
  };

  // Prepare fields object for render function
  const fields: Record<
    string,
    {
      value: string;
      invalid: boolean;
      onChange: (value: string, invalid?: boolean) => void;
    }
  > = {};

  Object.keys(formState).forEach((fieldName) => {
    fields[fieldName] = {
      ...formState[fieldName],
      onChange: getOnChange(fieldName),
    };
  });

  // Calculate steps excluding welcome pages for the indicator
  const nonWelcomeSteps = steps.filter((step) => !step.welcomePage);
  const firstStepIsWelcome = steps[0]?.welcomePage;

  // Don't show indicator on first step if it's a welcome page
  const shouldShowStepIndicator = !(currentStep === 0 && firstStepIsWelcome);

  // Calculate adjusted current step for indicator (excluding welcome pages before it)
  const adjustedCurrentStep = steps
    .slice(0, currentStep)
    .filter((step) => !step.welcomePage).length;

  return (
    <>
      {typeof notice === 'string' && <NoticeMessage>{notice}</NoticeMessage>}
      <div>
        {shouldShowStepIndicator && (
          <StepCounter>
            {stepCounterLabel} {adjustedCurrentStep + 1} of {nonWelcomeSteps.length}
          </StepCounter>
        )}
        {currentStepConfig.title && (
          <StepTitle centered={true}>{currentStepConfig.title}</StepTitle>
        )}
      </div>
      {currentStepConfig.instructions && (
        <Instructions spacing={currentStepConfig.spacing}>
          {currentStepConfig.instructions}
        </Instructions>
      )}
      {currentStepConfig.instructionsLink && (
        <InstructionCallout link={currentStepConfig.instructionsLink} />
      )}
      {currentStepConfig.divider && (
        <Divider spacing={currentStepConfig.spacing} />
      )}
      <StyledForm
        aria-label={formLabel ?? 'Multi-Step Sign In Form'}
        onSubmit={handleSubmit}
      >
        {render(fields, currentStep)}
        <ButtonContainer>
          {shouldShowBackButton() && <GoBackButton onClick={handleBack} />}
          <LoaderButton
            text={getButtonText()}
            isLoading={isLoading}
            disabled={isLoading || !validateCurrentStep()}
            type="submit"
          />
        </ButtonContainer>
        {shouldShowStepIndicator && (
          <StepIndicator
            totalSteps={nonWelcomeSteps.length}
            currentStep={adjustedCurrentStep}
          />
        )}
      </StyledForm>
    </>
  );
};

export default MultiSignInBase;
